import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Row } from '@tanstack/react-table';
import type { RowExpansionOptions, TableRowData } from '../types';

interface UseRowExpansionOptions<TRow extends TableRowData> {
  rows: Row<TRow>[];
  expandedRowIds: Set<string> | undefined;
  onToggleRowExpansion: ((rowId: string) => void) | undefined;
  onRowClick: ((row: TRow) => void) | undefined;
  rowExpansion: RowExpansionOptions<TRow> | undefined;
  enableRowExpansion: boolean;
}

interface UseRowExpansionReturn<TRow extends TableRowData> {
  getExpansionId: (row: Row<TRow>) => string;
  isClickable: boolean;
  handleRowClick: (row: Row<TRow>) => void;
  handleChevronClick: (event: React.MouseEvent, row: Row<TRow>) => void;
  canExpandRow: (row: Row<TRow>) => boolean;
  highlightedRowIds: Set<string>;
  scrollTargetRowId: string | null;
  scrollTargetRef: React.RefObject<HTMLTableRowElement | null>;
}

export function useDataTableRowExpansion<TRow extends TableRowData>({
  rows,
  expandedRowIds,
  onToggleRowExpansion,
  onRowClick,
  rowExpansion,
  enableRowExpansion,
}: UseRowExpansionOptions<TRow>): UseRowExpansionReturn<TRow> {
  const expandOnRowClick = rowExpansion?.expandOnRowClick ?? true;
  const getRowId = rowExpansion?.getRowId;
  const highlightOnExpand = rowExpansion?.highlightOnExpand ?? false;
  const highlightDuration = rowExpansion?.highlightDuration ?? 3000;
  const scrollToExpandedRow = rowExpansion?.scrollToExpandedRow ?? false;

  const [scrollTargetRowId, setScrollTargetRowId] = useState<string | null>(
    null
  );
  const hasScrolled = useRef(false);
  const scrollTargetRef = useRef<HTMLTableRowElement | null>(null);

  const [highlightedRowIds, setHighlightedRowIds] = useState<Set<string>>(
    new Set()
  );
  const prevExpandedRowIds = useRef<Set<string>>(new Set());
  const skipNextHighlightRef = useRef(false);

  const isClickable = useMemo(
    () => (enableRowExpansion && expandOnRowClick) || Boolean(onRowClick),
    [enableRowExpansion, expandOnRowClick, onRowClick]
  );

  const getExpansionId = useCallback(
    (row: Row<TRow>): string => (getRowId ? getRowId(row.original) : row.id),
    [getRowId]
  );

  const canExpandRow = useCallback(
    (row: Row<TRow>): boolean =>
      rowExpansion?.canExpand ? rowExpansion.canExpand(row.original) : true,
    [rowExpansion]
  );

  const handleRowClick = useCallback(
    (row: Row<TRow>) => {
      if (enableRowExpansion && expandOnRowClick && canExpandRow(row)) {
        skipNextHighlightRef.current = true;
        onToggleRowExpansion?.(getExpansionId(row));
      }
      onRowClick?.(row.original);
    },
    [
      enableRowExpansion,
      expandOnRowClick,
      onToggleRowExpansion,
      onRowClick,
      getExpansionId,
      canExpandRow,
    ]
  );

  const handleChevronClick = useCallback(
    (event: React.MouseEvent, row: Row<TRow>) => {
      event.stopPropagation();
      if (!canExpandRow(row)) return;
      skipNextHighlightRef.current = true;
      onToggleRowExpansion?.(getExpansionId(row));
    },
    [onToggleRowExpansion, getExpansionId, canExpandRow]
  );

  // --- Highlight on expand ---

  useEffect(() => {
    if (!highlightOnExpand || !expandedRowIds) return;
    const prev = prevExpandedRowIds.current;
    const newlyExpanded: string[] = [];
    for (const id of expandedRowIds) {
      if (!prev.has(id)) newlyExpanded.push(id);
    }
    prevExpandedRowIds.current = new Set(expandedRowIds);
    if (newlyExpanded.length === 0) return;

    if (skipNextHighlightRef.current) {
      skipNextHighlightRef.current = false;
      return;
    }

    // Deferred to callbacks to satisfy react-hooks/set-state-in-effect.
    const addTimer = setTimeout(() => {
      setHighlightedRowIds(current => {
        const next = new Set(current);
        newlyExpanded.forEach(id => next.add(id));
        return next;
      });
    }, 0);

    const removeTimer = setTimeout(() => {
      setHighlightedRowIds(current => {
        const next = new Set(current);
        newlyExpanded.forEach(id => next.delete(id));
        return next;
      });
    }, highlightDuration);

    return () => {
      clearTimeout(addTimer);
      clearTimeout(removeTimer);
    };
  }, [expandedRowIds, highlightOnExpand, highlightDuration]);

  useEffect(() => {
    if (!scrollToExpandedRow || hasScrolled.current || !expandedRowIds?.size) {
      return;
    }
    const targetRow = rows.find(row => expandedRowIds.has(getExpansionId(row)));
    if (!targetRow) return;
    const targetId = getExpansionId(targetRow);
    // Deferred to a callback to satisfy react-hooks/set-state-in-effect.
    const timer = setTimeout(() => {
      setScrollTargetRowId(targetId);
    }, 0);
    return () => clearTimeout(timer);
  }, [rows, expandedRowIds, scrollToExpandedRow, getExpansionId]);

  useEffect(() => {
    if (!scrollTargetRowId || !scrollTargetRef.current) return;
    scrollTargetRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    hasScrolled.current = true;
    // scrollTargetRowId is intentionally left set; hasScrolled prevents re-scroll.
  }, [scrollTargetRowId]);

  return {
    getExpansionId,
    isClickable,
    handleRowClick,
    handleChevronClick,
    canExpandRow,
    highlightedRowIds,
    scrollTargetRowId,
    scrollTargetRef,
  };
}
